/**
 * useUserIdentity — identidade da sessão para o menu de usuário do header (GUPM-212).
 *
 * O shell (Header) precisa do nome da pessoa, mas não deve importar um hook de
 * feature (`useCurrentEngineer` vive em `features/shift-handover`). Então lê o
 * nome pelo datasource, **reusando o mesmo queryKey** `['current-engineer', id]`
 * — assim o TanStack Query deduplica com a query que o `ShiftHandoverButton` já
 * dispara, sem chamada de rede extra.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, type SignOutResult, type UserProfile } from '@/hooks/useAuth';
import { useDataSources } from '@/services/datasources';

export interface UserIdentity {
  /** Nome exibível (do cadastro em `app_users`); null enquanto carrega/ausente. */
  name: string | null;
  email: string | null;
  profile: UserProfile | null;
  /** Devolve `{ error }` — o logout pode falhar sem encerrar a sessão (GUPM-255). */
  signOut: () => Promise<SignOutResult>;
}

export function useUserIdentity(): UserIdentity {
  const { user, profile, signOut } = useAuth();
  const { shiftHandover } = useDataSources();

  const { data, error } = useQuery({
    queryKey: ['current-engineer', user?.id ?? 'anonymous'],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: () => shiftHandover.getCurrentUser(),
  });

  // A tela degrada graciosamente (email/perfil vêm da sessão; o nome cai em
  // "Usuário"), mas o erro não some em silêncio — mesmo tratamento que o Header
  // dá ao `scopeError`. Não loga PII: `error` é falha de query (RLS/rede).
  useEffect(() => {
    if (error) console.warn('[useUserIdentity] falha ao carregar o nome do usuário:', error);
  }, [error]);

  return {
    // `|| null` (não `??`): nome vazio do cadastro também cai no fallback "Usuário".
    name: data?.name || null,
    email: user?.email ?? data?.email ?? null,
    profile: profile ?? data?.profile ?? null,
    signOut,
  };
}
